import { getAllSceneNodes, getSceneNodeChildren, type CurvaDocument } from "@/document/model/document";
import { frameAutoLayoutMode, frameFlexLayout, type ComponentInstanceNode, type ComponentNode, type ComponentVariantNode, type FrameNode, type SceneNode } from "@/document/model/nodes";
import { cloneSceneNodeWithFreshIds } from "./cloneSceneNode";
import { contoursToPathData, pathDataToContours, transformContours } from "./pathGeometry";
import { getVectorNetworkBounds } from "./vectorGeometry";
import type { Rect } from "@/lib/math/rect";
import { transformRotationOrigin } from "@/lib/math/rotation";

export function createInstanceFromComponent(
  component: ComponentNode,
  offset: { x: number; y: number } = { x: 0, y: 0 },
  variantId?: string,
): ComponentInstanceNode {
  const selectedVariant = variantId
    ? componentVariants(component).find(variant => variant.id === variantId)
    : undefined;
  if (variantId && !selectedVariant) {
    throw new Error(`Variant "${variantId}" does not belong to component "${component.id}".`);
  }
  const source = selectedVariant
    ? { ...component, children: [selectedVariant, ...component.children.filter(variant => variant.id !== selectedVariant.id)] }
    : component;
  const instance = cloneSceneNodeWithFreshIds(source, { offset, instantiateComponent: true }) as ComponentInstanceNode;
  return instance;
}

export function componentVariants(component: ComponentNode): ComponentVariantNode[] {
  return component.children.filter((node): node is ComponentVariantNode => node.type === "componentVariant");
}

/**
 * Rebuilds every instance from its variant, re-applying per-instance overrides.
 *
 * Returns the ids of nodes inside instances that must be re-laid-out by the
 * caller's next auto-layout pass:
 *  - auto-sized text carrying a `text` override (its intrinsic size depends on
 *    the overridden content, not the variant default), and
 *  - the root flex frame of a reflow instance (resized instances rebuild at the
 *    variant's natural child sizes and rely on auto-layout to redistribute).
 *
 * `finalizeDocumentGeometry` must settle auto-layout again once this returns a
 * non-empty list, otherwise hug containers keep the variant-default size and
 * reflow roots never redistribute their children.
 */
export function refreshComponentInstances(document: CurvaDocument, changedNodeIds: Iterable<string> = []): string[] {
  const changed = new Set(changedNodeIds);
  const components = new Map(getAllSceneNodes(document).filter((node): node is ComponentNode => node.type === "component").map(node => [node.id, node]));
  const relayoutIds: string[] = [];
  if (components.size === 0) {
    return relayoutIds;
  }

  for (const instance of getAllSceneNodes(document).filter((node): node is ComponentInstanceNode => node.type === "instance")) {
    const component = components.get(instance.componentId);
    if (!component) {
      continue;
    }
    const variant = componentVariants(component).find(candidate => candidate.id === instance.variantId) ?? componentVariants(component)[0];
    if (!variant) {
      continue;
    }

    captureChangedInstanceOverrides(instance, variant, changed);
    const reflowRootId = syncInstanceFromVariant(instance, variant);
    if (reflowRootId) {
      relayoutIds.push(reflowRootId);
    }
    collectOverriddenAutoTextIds(instance, relayoutIds);
  }
  return relayoutIds;
}

function collectOverriddenAutoTextIds(instance: ComponentInstanceNode, out: string[]): void {
  for (const node of flattenChildren(instance.children)) {
    if (node.type !== "text" || node.text.textLayout === "FIXED_SIZE") {
      continue;
    }
    const sourceId = node.componentSourceId;
    if (sourceId && instance.overrides[sourceId]?.text !== undefined) {
      out.push(node.id);
    }
  }
}

function captureChangedInstanceOverrides(instance: ComponentInstanceNode, variant: ComponentVariantNode, changedNodeIds: Set<string>): void {
  if (changedNodeIds.size === 0) {
    return;
  }

  const sourceById = new Map(flattenChildren(variant.children).map(node => [node.id, node]));
  for (const node of flattenChildren(instance.children)) {
    if (!changedNodeIds.has(node.id) || !node.componentSourceId) {
      continue;
    }

    const source = sourceById.get(node.componentSourceId);
    if (!source) {
      continue;
    }

    const expected = cloneSourceNodeForInstance(source, instance, variant);
    const override = { ...(instance.overrides[node.componentSourceId] ?? {}) };
    const hadStyleOverride = override.style !== undefined;
    // Auto-sized text derives its bounds from content, so never pin a bounds
    // override for it — that would freeze the box at the size measured when the
    // text was edited and defeat re-measurement of the overridden characters.
    const isAutoSizedText = node.type === "text" && node.text.textLayout !== "FIXED_SIZE";
    // Reflow instances let auto-layout own every child's geometry, so pinning a
    // bounds override would freeze a child at its just-reflowed size and stop it
    // from redistributing on the next resize.
    const reflow = instanceReflowRoot(instance, variant) !== undefined;
    if (!isAutoSizedText && !reflow && !rectEqual(node.bounds, expected.bounds)) {
      override.bounds = structuredClone(node.bounds);
    } else {
      delete override.bounds;
    }
    if (!transformEqual(node.transform, expected.transform)) {
      override.transform = structuredClone(node.transform);
    } else {
      delete override.transform;
    }
    if (JSON.stringify(node.style) !== JSON.stringify(expected.style) || hadStyleOverride) {
      override.style = structuredClone(node.style);
    } else {
      delete override.style;
    }
    // Style-library bindings live on the instance child, not in `style`. Capture
    // them relative to the variant source so a token bound to an instance (e.g.
    // via apply_style) isn't dropped when the instance rebuilds. An empty object
    // records an explicit clear of a binding the source still carries.
    if (JSON.stringify(node.styleBindings ?? null) !== JSON.stringify(expected.styleBindings ?? null)) {
      override.styleBindings = node.styleBindings ? structuredClone(node.styleBindings) : {};
    } else {
      delete override.styleBindings;
    }
    if (node.name !== expected.name) {
      override.name = node.name;
    } else {
      delete override.name;
    }
    if (node.visible !== expected.visible) {
      override.visible = node.visible;
    } else {
      delete override.visible;
    }
    if (node.locked !== expected.locked) {
      override.locked = node.locked;
    } else {
      delete override.locked;
    }
    if (node.type === "primitive" && expected.type === "primitive" && JSON.stringify(node.primitive) !== JSON.stringify(expected.primitive)) {
      override.primitive = structuredClone(node.primitive);
    } else {
      delete override.primitive;
    }
    if (node.type === "text" && expected.type === "text" && JSON.stringify(node.text) !== JSON.stringify(expected.text)) {
      override.text = structuredClone(node.text);
    } else {
      delete override.text;
    }

    if (Object.keys(override).length === 0) {
      delete instance.overrides[node.componentSourceId];
    } else {
      instance.overrides[node.componentSourceId] = override;
    }
  }
}

/**
 * Rebuilds an instance's children from its variant.
 *
 * Returns the id of the instance's root flex frame when the instance reflowed
 * (see {@link instanceReflowRoot}) so the caller can force an auto-layout pass
 * that redistributes its children; returns `undefined` for scaling instances.
 */
function syncInstanceFromVariant(instance: ComponentInstanceNode, variant: ComponentVariantNode): string | undefined {
  const previousBySource = new Map(flattenChildren(instance.children).flatMap(node => node.componentSourceId ? [[node.componentSourceId, node.id] as const] : []));
  const reflowRoot = instanceReflowRoot(instance, variant);
  // Instances that have not been explicitly resized by the user track the variant's size so that
  // auto-layout hug changes (e.g. text width growing) propagate correctly.
  if (!instance.explicitBounds || instance.bounds.width === 0 || instance.bounds.height === 0) {
    instance.bounds = {
      x: instance.bounds.x,
      y: instance.bounds.y,
      width: variant.bounds.width,
      height: variant.bounds.height,
    };
  }
  instance.children = variant.children.map(child => cloneSourceSubtreeForInstance(child, instance, variant, previousBySource));

  if (!reflowRoot) {
    return undefined;
  }
  // The subtree was cloned at the variant's natural sizes (translate-only, see
  // instanceGeometryTarget). Grow/shrink only the root frame's fixed axes to the
  // instance box; auto-layout then redistributes children instead of scaling
  // every descendant (which would stretch fixed-size buttons and icons).
  const rootClone = instance.children[0];
  if (rootClone?.type !== "frame") {
    return undefined;
  }
  const layout = frameFlexLayout(reflowRoot);
  rootClone.bounds = {
    ...rootClone.bounds,
    width: layout.widthSizing === "fixed" ? instance.bounds.width : rootClone.bounds.width,
    height: layout.heightSizing === "fixed" ? instance.bounds.height : rootClone.bounds.height,
  };
  return rootClone.id;
}

/**
 * The variant's single auto-layout root frame when this instance should reflow
 * (not scale) to its box, otherwise `undefined`.
 *
 * Reflow applies only to explicitly-resized instances whose variant is exactly
 * one flex frame. Then descendants keep their real sizes and Yoga redistributes
 * the extra space; a uniform scale would instead stretch buttons/icons/text.
 * Scaling remains the right behavior for icon/vector components and any variant
 * that is not a single flex container.
 */
function instanceReflowRoot(instance: ComponentInstanceNode, variant: ComponentVariantNode): FrameNode | undefined {
  if (!instance.explicitBounds || instance.bounds.width === 0 || instance.bounds.height === 0) {
    return undefined;
  }
  if (variant.children.length !== 1) {
    return undefined;
  }
  const [root] = variant.children;
  return root && root.type === "frame" && frameAutoLayoutMode(root) === "flex" ? root : undefined;
}

/**
 * Bounds the variant's geometry is mapped into for this instance.
 *
 * Scaling instances map to the instance box (a uniform affine scale). Reflow
 * instances map to a box the size of the variant placed at the instance origin,
 * i.e. translate-only (scale 1): children keep their natural sizes and
 * {@link syncInstanceFromVariant} resizes just the root frame afterwards.
 */
function instanceGeometryTarget(instance: ComponentInstanceNode, variant: ComponentVariantNode): Rect {
  if (instanceReflowRoot(instance, variant)) {
    return {
      x: instance.bounds.x,
      y: instance.bounds.y,
      width: variant.bounds.width,
      height: variant.bounds.height,
    };
  }
  return instance.bounds;
}

function cloneSourceSubtreeForInstance(
  source: SceneNode,
  instance: ComponentInstanceNode,
  variant: ComponentVariantNode,
  previousBySource: Map<string, string>,
): SceneNode {
  const clone = cloneSourceNodeForInstance(source, instance, variant);
  clone.id = previousBySource.get(source.id) ?? clone.id;
  clone.componentSourceId = source.id;
  const override = instance.overrides[source.id];
  if (override) {
    if (override.bounds) {
      clone.bounds = structuredClone(override.bounds);
    }
    if (override.transform) {
      clone.transform = structuredClone(override.transform);
    }
    if (override.style) {
      clone.style = structuredClone(override.style);
    }
    if (override.styleBindings !== undefined) {
      clone.styleBindings = Object.keys(override.styleBindings).length > 0
        ? structuredClone(override.styleBindings)
        : undefined;
    }
    if (override.name !== undefined) {
      clone.name = override.name;
    }
    if (override.visible !== undefined) {
      clone.visible = override.visible;
    }
    if (override.locked !== undefined) {
      clone.locked = override.locked;
    }
    if (clone.type === "primitive" && override.primitive) {
      clone.primitive = structuredClone(override.primitive);
    }
    if (clone.type === "text" && override.text) {
      clone.text = structuredClone(override.text);
    }
  }

  const children = getSceneNodeChildren(source);
  const cloneChildren = getSceneNodeChildren(clone);
  if (children && cloneChildren) {
    cloneChildren.splice(0, cloneChildren.length, ...children.map(child => cloneSourceSubtreeForInstance(child, instance, variant, previousBySource)));
    if ((clone.type === "group" || clone.type === "component" || clone.type === "componentVariant" || clone.type === "instance") && cloneChildren.length > 0 && !override?.bounds) {
      const bounds = getNodesBounds(cloneChildren);
      if (bounds) {
        clone.bounds = bounds;
      }
    }
  }

  return clone;
}

function cloneSourceNodeForInstance(source: SceneNode, instance: ComponentInstanceNode, variant: ComponentVariantNode): SceneNode {
  const clone = cloneSceneNodeWithFreshIds(source);
  transformNodeToInstanceSpace(clone, variant.bounds, instanceGeometryTarget(instance, variant));
  return clone;
}

function transformNodeToInstanceSpace(node: SceneNode, componentBounds: Rect, instanceBounds: Rect): void {
  const fromBounds = node.bounds;
  const toBounds = transformBounds(node.bounds, componentBounds, instanceBounds);
  transformNodeGeometry(node, fromBounds, toBounds);
  const children = getSceneNodeChildren(node);
  if (children) {
    for (const child of children) {
      transformNodeToInstanceSpace(child, componentBounds, instanceBounds);
    }
  }
}

function transformNodeGeometry(node: SceneNode, fromBounds: Rect, toBounds: Rect): void {
  if (node.type === "shape") {
    node.path = contoursToPathData(transformContours(pathDataToContours(node.path), fromBounds, toBounds));
  }
  if (node.type === "vectorNetwork") {
    const transformPoint = pointTransformer(fromBounds, toBounds);
    for (const vertex of node.network.vertices) {
      const next = transformPoint(vertex);
      vertex.x = next.x;
      vertex.y = next.y;
    }
    for (const edge of node.network.edges) {
      if (edge.from.handle) {
        edge.from.handle = transformPoint(edge.from.handle);
      }
      if (edge.to.handle) {
        edge.to.handle = transformPoint(edge.to.handle);
      }
    }
    node.bounds = getVectorNetworkBounds(node.network);
    return;
  }
  if (node.type === "boolean") {
    const transformed = transformContours(node.resolved.contours, fromBounds, toBounds);
    node.resolved = {
      contours: transformed,
      path: contoursToPathData(transformed),
    };
  }
  if (node.transform.rotation !== 0) {
    const transformPoint = pointTransformer(fromBounds, toBounds);
    const origin = transformPoint(transformRotationOrigin(fromBounds, node.transform));
    node.transform = { ...node.transform, x: origin.x, y: origin.y };
  }
  node.bounds = toBounds;
}

function transformBounds(bounds: Rect, from: Rect, to: Rect): Rect {
  const scaleX = from.width === 0 ? 1 : to.width / from.width;
  const scaleY = from.height === 0 ? 1 : to.height / from.height;
  return {
    x: to.x + (bounds.x - from.x) * scaleX,
    y: to.y + (bounds.y - from.y) * scaleY,
    width: Math.max(0, bounds.width * scaleX),
    height: Math.max(0, bounds.height * scaleY),
  };
}

function pointTransformer(fromBounds: Rect, toBounds: Rect): (point: { x: number; y: number }) => { x: number; y: number } {
  const scaleX = fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width;
  const scaleY = fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height;
  return point => ({
    x: toBounds.x + (point.x - fromBounds.x) * scaleX,
    y: toBounds.y + (point.y - fromBounds.y) * scaleY,
  });
}

function flattenChildren(children: SceneNode[]): SceneNode[] {
  return children.flatMap(node => {
    const nested = getSceneNodeChildren(node);
    return nested ? [node, ...flattenChildren(nested)] : [node];
  });
}

function getNodesBounds(nodes: SceneNode[]): Rect | undefined {
  if (nodes.length === 0) {
    return undefined;
  }

  const left = Math.min(...nodes.map(node => node.bounds.x));
  const top = Math.min(...nodes.map(node => node.bounds.y));
  const right = Math.max(...nodes.map(node => node.bounds.x + node.bounds.width));
  const bottom = Math.max(...nodes.map(node => node.bounds.y + node.bounds.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

function rectEqual(left: Rect, right: Rect): boolean {
  return left.x === right.x && left.y === right.y && left.width === right.width && left.height === right.height;
}

function transformEqual(left: SceneNode["transform"], right: SceneNode["transform"]): boolean {
  return left.x === right.x &&
    left.y === right.y &&
    left.rotation === right.rotation &&
    left.scaleX === right.scaleX &&
    left.scaleY === right.scaleY;
}
